import React, { useEffect, useState } from "react";
import { Brain, Check, MessageCircle, Mail, Globe } from "lucide-react";

/**
 * Animated multi-turn conversation: rotates through
 * channel-routed user messages + AI replies with typing indicators.
 */
const script = [
  {
    channel: "whatsapp",
    user: "Hey, I was charged twice for order #A-2381.",
    ai: "I see the duplicate charge — refunding ₹2,499 to your card ending 4421. You'll see it within 3 business days. Anything else I can help with?",
  },
  {
    channel: "email",
    user: "Can you resend the invoice for October?",
    ai: "Done. I've emailed invoice INV-10/2025 to priya@acme.com and added it to your Billing portal.",
  },
  {
    channel: "web",
    user: "My dashboard isn't loading agent runs.",
    ai: "Thanks — I pulled your logs. Looks like a rate-limit on your Gemini key. I'm rotating to the backup and re-queuing 14 failed runs.",
  },
];

const channelMeta = {
  whatsapp: { icon: MessageCircle, color: "#25D366", label: "WhatsApp" },
  email: { icon: Mail, color: "#8AB4F8", label: "Email" },
  web: { icon: Globe, color: "#3DDFB3", label: "Web Chat" },
};

export default function ChatDemo() {
  const [step, setStep] = useState(0); // 0=user, 1=typing, 2=ai
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timers = [];
    // user message visible for 1.6s -> typing dots 1.6s -> AI reply for 3s -> next
    if (step === 0) timers.push(setTimeout(() => setStep(1), 1600));
    if (step === 1) timers.push(setTimeout(() => setStep(2), 1600));
    if (step === 2)
      timers.push(
        setTimeout(() => {
          setIdx((i) => (i + 1) % script.length);
          setStep(0);
        }, 3800)
      );
    return () => timers.forEach(clearTimeout);
  }, [step, idx]);

  const cur = script[idx];
  const meta = channelMeta[cur.channel];

  return (
    <div
      data-testid="hero-chat-demo"
      className="relative"
    >
      {/* Decorative halo */}
      <div className="pointer-events-none absolute -inset-8 rounded-[28px] bg-gradient-to-br from-[#7C5CFF]/20 via-transparent to-[#3DDFB3]/10 blur-2xl" />

      <div className="relative rounded-[22px] border border-white/10 bg-[#0d0d14] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
        {/* Window chrome */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/5 bg-white/[0.02]">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ef4444]/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b]/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#22c55e]/80" />
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono tracking-[0.12em] uppercase text-white/50">
            <meta.icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
            <span>Incoming · {meta.label}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-[#3DDFB3]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3DDFB3] soft-pulse" />
            Live
          </div>
        </div>

        {/* Conversation */}
        <div className="px-5 py-6 space-y-4 min-h-[420px]">
          {/* Customer message */}
          <div
            key={`u-${idx}`}
            className="flex items-start gap-3 fade-up"
            data-testid="chat-user-msg"
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white"
              style={{ background: "#27272a" }}
            >
              PS
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 text-[11px] text-white/40 mb-1.5">
                <span>Priya Sharma</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1">
                  <meta.icon className="w-3 h-3" style={{ color: meta.color }} />
                  {meta.label}
                </span>
              </div>
              <div className="inline-block max-w-[85%] px-4 py-2.5 rounded-2xl rounded-tl-sm bg-white/[0.05] border border-white/5 text-[14px] text-white/90 leading-relaxed">
                {cur.user}
              </div>
            </div>
          </div>

          {/* Typing indicator */}
          {step >= 1 && (
            <div className="flex items-start gap-3 fade-up" data-testid="chat-typing">
              <div className="w-8 h-8 rounded-lg bg-[#7C5CFF] flex items-center justify-center shadow-[0_0_16px_rgba(124,92,255,0.45)]">
                <Brain className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[11px] text-white/40 mb-1.5">
                  <span>CRM Agent</span>
                  <span>·</span>
                  <span className="text-[#7C5CFF]">Reasoning</span>
                </div>
                {step === 1 ? (
                  <div className="inline-flex items-center gap-1.5 px-4 py-3 rounded-2xl rounded-tl-sm bg-[#7C5CFF]/15 border border-[#7C5CFF]/25">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7C5CFF] typing-dot" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7C5CFF] typing-dot" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[#7C5CFF] typing-dot" />
                  </div>
                ) : (
                  <div className="inline-block max-w-[90%] px-4 py-2.5 rounded-2xl rounded-tl-sm bg-[#7C5CFF]/12 border border-[#7C5CFF]/25 text-[14px] text-white/95 leading-relaxed fade-up">
                    {cur.ai}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AI actions trace (shown when ai reply visible) */}
          {step === 2 && (
            <div
              className="ml-11 flex flex-wrap items-center gap-2 fade-up"
              data-testid="chat-ai-actions"
            >
              {[
                "checked_orders_kb",
                "processed_refund($2,499)",
                "logged_ticket",
              ].map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#3DDFB3]/10 border border-[#3DDFB3]/20 text-[11px] font-mono text-[#3DDFB3]"
                >
                  <Check className="w-3 h-3" />
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Footer meta strip */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-white/5 bg-white/[0.015] text-[11px] font-mono text-white/40 uppercase tracking-[0.12em]">
          <span>session · 0x8f12</span>
          <span>kb · refunds_policy_v3</span>
          <span className="text-[#3DDFB3]">resolved</span>
        </div>
      </div>
    </div>
  );
}
